import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
// @mui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';

import Label from 'src/components/label';
import { setNextVisits } from 'src/hooks/use-career-api';
import Iconify from 'src/components/iconify';
import { programIcon } from 'src/sections/off-campus/program-icons';
import { programName } from 'src/sections/off-campus/program-workflow';
import { fDate } from 'src/utils/format-time';

import CaseloadFilters from './caseload-filters';
import CaseloadTiles from './caseload-tiles';
import NextVisitField, { EMPTY_NEXT_VISIT } from './next-visit-field';
import { dayText, nextVisitPayload, nextVisitProblem, visitStatusLabel } from './next-visit';
import {
  EMPTY_FILTERS,
  applyCaseloadFilters,
  caseloadParams,
  parseCaseloadParams,
  sortCaseload,
  tileFilters,
} from './caseload';

// ----------------------------------------------------------------------

// The Careers dashboard's caseload (#612, #613): in one card, the attention
// tiles, the filters, then every student at the school in a table that filters, sorts and sets next
// visits for a selection. Filters and sort live in the URL's query string, so
// the back button returns to the same view after opening a student.

const COLUMNS = [
  { id: 'name', label: 'Name' },
  { id: 'grade', label: 'Grade' },
  { id: 'nextVisit', label: 'Next visit' },
  { id: 'lastVisit', label: 'Last visit' },
  { id: 'program', label: 'Off-campus' },
  { id: 'todos', label: 'To-dos' },
  { id: 'progress', label: 'Checklist' },
  { id: 'flags', label: 'Flags' },
];

const ROWS_PER_PAGE = [25, 50, 100];

// A day in the table, short: the year only when it isn't this one
const shortDay = (day, today) => fDate(`${day}T12:00:00`, day.slice(0, 4) === today.slice(0, 4) ? 'MMM d' : 'MMM d, yyyy');

// The next visit in the table: its status and day, kept short so the table fits
const NEXT_VISIT_PREFIX = { overdue: 'Overdue', dueSoon: 'Due soon' };
const nextVisitText = (r, next, today) => {
  if (!r.nextVisitBy || !['overdue', 'dueSoon', 'planned'].includes(next.status)) return next.text;
  const day = shortDay(r.nextVisitBy, today);
  return NEXT_VISIT_PREFIX[next.status] ? `${NEXT_VISIT_PREFIX[next.status]} · ${day}` : day;
};

const countGrades = (rows) => {
  const counts = { all: rows.length, graduating: rows.filter((r) => r.graduating).length };
  rows.forEach((r) => {
    counts[r.grade] = (counts[r.grade] || 0) + 1;
  });
  return counts;
};

// What the bulk write skipped, in words
const skippedText = (skipped) => {
  const graduation = skipped.filter((s) => s.code === 'NEXT_VISIT_AFTER_GRADUATION').length;
  const other = skipped.length - graduation;
  return [
    graduation && `${graduation} Grade 12 ${graduation === 1 ? 'student was' : 'students were'} left out because the date is after graduation.`,
    other && `${other} ${other === 1 ? 'student was' : 'students were'} skipped.`,
  ]
    .filter(Boolean)
    .join(' ');
};

const CaseloadCard = ({ rows, today, loading, error, schoolId, onChanged, ai = false }) => {
  // The Flags column comes from the AI career summary: only while that is on
  const columns = ai ? COLUMNS : COLUMNS.filter((c) => c.id !== 'flags');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { filters, sort, dir } = useMemo(() => parseCaseloadParams(searchParams), [searchParams]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selected, setSelected] = useState(() => new Set());

  // Bulk next visit
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkValue, setBulkValue] = useState(EMPTY_NEXT_VISIT);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkResult, setBulkResult] = useState(null);

  const update = (next) => {
    setSearchParams(caseloadParams({ filters, sort, dir, ...next }, searchParams), { replace: true });
    setPage(0);
  };
  const changeFilters = (partial) => update({ filters: { ...filters, ...partial } });
  const resetFilters = () => update({ filters: EMPTY_FILTERS });
  const applyTile = (tile) => update({ filters: tileFilters(tile) });
  const toggleSort = (id) => update({ sort: id, dir: sort === id && dir === 'asc' ? 'desc' : 'asc' });

  const shown = useMemo(() => sortCaseload(applyCaseloadFilters(rows, filters), sort, dir), [rows, filters, sort, dir]);
  const pageRows = shown.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const gradeCounts = useMemo(() => countGrades(rows), [rows]);

  const selectedShown = shown.filter((r) => selected.has(r.asn));
  const allShownSelected = shown.length > 0 && selectedShown.length === shown.length;

  const toggleAll = () => setSelected(allShownSelected ? new Set() : new Set(shown.map((r) => r.asn)));
  const toggleOne = (asn) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(asn)) next.delete(asn);
      else next.add(asn);
      return next;
    });

  const bulkPayload = nextVisitPayload(bulkValue, today);
  const bulkProblem = nextVisitProblem(bulkValue, { grade: null, today });

  const openBulk = () => {
    setBulkValue(EMPTY_NEXT_VISIT);
    setBulkError('');
    setBulkOpen(true);
  };

  const saveBulk = async () => {
    setBulkSaving(true);
    setBulkError('');
    try {
      const result = await setNextVisits(schoolId, { asns: selectedShown.map((r) => r.asn), ...bulkPayload });
      setBulkOpen(false);
      setSelected(new Set());
      setBulkResult(result);
      await onChanged();
    } catch (err) {
      setBulkError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'The next visits could not be saved.');
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          title="Caseload"
          sx={{ pb: 0 }}
          subheader={loading ? 'Loading students…' : `${shown.length} of ${rows.length} students`}
          action={
            selectedShown.length > 0 && (
              <Button variant="contained" size="small" onClick={openBulk}>
                {`Set next visit for ${selectedShown.length}`}
              </Button>
            )
          }
        />
        <CardContent sx={{ pt: 2.5 }}>
          <Stack spacing={2.5}>
          {!loading && !error && <CaseloadTiles rows={rows} filters={filters} onApply={applyTile} onClear={resetFilters} />}
          {error && <Alert severity="error">{error.message || 'The caseload could not be loaded.'}</Alert>}
          {bulkResult && (
            <Alert severity={bulkResult.skipped.length ? 'warning' : 'success'} onClose={() => setBulkResult(null)}>
              {`Next visit set for ${bulkResult.updated} ${bulkResult.updated === 1 ? 'student' : 'students'}. ${skippedText(bulkResult.skipped)}`}
            </Alert>
          )}
          {!error && (
            <CaseloadFilters
              rows={rows}
              filters={filters}
              gradeCounts={gradeCounts}
              onChange={changeFilters}
              onReset={resetFilters}
              ai={ai}
            />
          )}
          </Stack>
        </CardContent>

        {!error && !loading && (
          <>
            <Box sx={{ overflowX: 'auto' }}>
              {/* Narrower cell padding, so every column fits a laptop screen */}
              <Table size="small" sx={{ '& th:not(:first-of-type), & td:not(:first-of-type)': { px: 1.25 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allShownSelected}
                        indeterminate={selectedShown.length > 0 && !allShownSelected}
                        onChange={toggleAll}
                        inputProps={{ 'aria-label': 'Select all students shown' }}
                      />
                    </TableCell>
                    {columns.map((c) => (
                      <TableCell key={c.id} sortDirection={sort === c.id ? dir : false} sx={{ whiteSpace: 'nowrap' }}>
                        <TableSortLabel active={sort === c.id} direction={sort === c.id ? dir : 'asc'} onClick={() => toggleSort(c.id)}>
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((r) => {
                    const next = visitStatusLabel({ by: r.nextVisitBy, noFollowUp: r.visitStatus === 'noFollowUp' }, today);
                    return (
                      <TableRow
                        key={r.asn}
                        hover
                        selected={selected.has(r.asn)}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/student/${r.asn}?tab=career`)}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox size="small" checked={selected.has(r.asn)} onChange={() => toggleOne(r.asn)} inputProps={{ 'aria-label': `Select ${r.name}` }} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{r.name || '—'}</TableCell>
                        <TableCell>{r.grade}</TableCell>
                        <TableCell>
                          <Label color={next.color} sx={{ whiteSpace: 'nowrap' }}>
                            {nextVisitText(r, next, today)}
                          </Label>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {r.lastVisitDate ? `${shortDay(r.lastVisitDate, today)} · ${r.daysSinceVisit}d` : 'Never'}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {/* The program's code keeps the column narrow; its name is in the tooltip */}
                            {r.programs.map((p) => (
                              <Tooltip key={p} title={programName(p)}>
                                <span>
                                  <Label variant="soft" startIcon={<Iconify icon={programIcon(p)} />}>
                                    {p}
                                  </Label>
                                </span>
                              </Tooltip>
                            ))}
                            {r.referralPending && <Label color="warning">Referral pending</Label>}
                            {r.placementEndsInDays !== null && r.placementEndsInDays <= 30 && (
                              <Label color="info">{`Ends in ${r.placementEndsInDays}d`}</Label>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {r.openTodos ? (
                            <Label color={r.overdueTodos ? 'error' : 'default'} sx={{ whiteSpace: 'nowrap' }}>
                              {r.overdueTodos ? `${r.openTodos} open · ${r.overdueTodos} overdue` : `${r.openTodos} open`}
                            </Label>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>{`${r.checklistPercent}%`}</TableCell>
                        {ai && (
                          <TableCell>
                            {r.flags.length ? (
                              <Tooltip title={r.flags.join(' · ')}>
                                <span>
                                  <Label color="warning">{r.flags.length}</Label>
                                </span>
                              </Tooltip>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            {shown.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                No students match these filters.
              </Typography>
            )}
            <TablePagination
              component="div"
              count={shown.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={ROWS_PER_PAGE}
            />
          </>
        )}
      </Card>

      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{`Next visit for ${selectedShown.length} ${selectedShown.length === 1 ? 'student' : 'students'}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <NextVisitField value={bulkValue} onChange={setBulkValue} today={today} disabled={bulkSaving} />
            {bulkPayload?.by && (
              <Typography variant="caption" color="text.secondary">
                {`Sets ${dayText(bulkPayload.by)} for each. Any Grade 12 student it is after graduation for is left out.`}
              </Typography>
            )}
            {bulkError && <Alert severity="error">{bulkError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setBulkOpen(false)}>
            Cancel
          </Button>
          <LoadingButton variant="contained" loading={bulkSaving} disabled={!bulkPayload || !!bulkProblem} onClick={saveBulk}>
            Set next visit
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

CaseloadCard.propTypes = {
  ai: PropTypes.bool,
  rows: PropTypes.array.isRequired,
  today: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.object,
  schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChanged: PropTypes.func.isRequired,
};

export default CaseloadCard;
